import { SerializedStyles } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Ref, memo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { usePropertyValuation } from "src/apps/AppFlow/pages/hooks/usePropertyValuation";
import { Chip, FormFlowPage, Skeleton, TextInput, Touchable } from "src/components";
import { BoldText, RegularText } from "src/components/v1";
import useMergedRef from "src/hooks/useMergedRef";
import { minCurrency } from "src/validators";
import * as z from "zod";

import { formatters } from "@fraction/shared";

import { useNoSubmit } from "../hooks";
import { styles } from "../styles";

export const MINIMUM_HOME_VALUE = 125000;

export interface HomeValueProps {
  value?: string;
  onChange?: (value?: string) => void;
  minHomeValue?: number;
  propertyAddress?: string;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  style?: SerializedStyles;
}

const HomeValue = ({
  value,
  onChange,
  onNext,
  propertyAddress,
  style,
  focusRef,
  minHomeValue = MINIMUM_HOME_VALUE,
  ...props
}: HomeValueProps) => {
  const schema = z.object({
    propertyValue: minCurrency(
      minHomeValue,
      `Sorry, Fraction only serves homes worth over ${formatters.number.getCurrencyFromNumber(minHomeValue, {
        inputInDollars: true,
      })}`
    ),
  });
  const { register, handleSubmit, errors } = useForm({
    resolver: zodResolver(schema),
  });

  const guessedValue = usePropertyValuation(propertyAddress);

  const validate = useCallback(() => !!value && !errors?.propertyValue?.message, [value, errors]);
  const { submitHandler, nextDisabled } = useNoSubmit({ onNext, validate });

  const mergedFocus = useMergedRef(register, focusRef);
  const mergedFocusRef = focusRef ? mergedFocus : register;

  const setToGuessedValue = useCallback(() => {
    if (!guessedValue?.data) {
      return;
    }
    onChange?.((guessedValue.data / 100).toString());
  }, [onChange, guessedValue?.data]);

  return (
    <FormFlowPage
      {...props}
      isStickyDesktopFooter
      title={`How much is ${propertyAddress || "your home"} worth?`}
      description={
        <RegularText>
          Don’t know the exact value?{" "}
          {guessedValue?.isLoading ? (
            <Skeleton className="h-[18px] w-[300px]" />
          ) : guessedValue?.data ? (
            <>
              <RegularText paragraph>
                Fraction estimates the home value at{" "}
                <BoldText paragraph>
                  {formatters.number.getCurrencyFromNumber(guessedValue?.data, { digits: 0 })}
                </BoldText>
                .
              </RegularText>
            </>
          ) : (
            "Your best guess should do the trick."
          )}
        </RegularText>
      }
      onNext={handleSubmit(submitHandler)}
      style={style}
      nextDisabled={nextDisabled}
    >
      <TextInput
        error={errors?.propertyValue?.message}
        ref={mergedFocusRef}
        maskOptions={{
          rightAlign: false,
          alias: "currency",
          prefix: "$ ",
          jitMasking: false,
          digits: 0,
        }}
        min={0}
        max={100000000}
        style={styles.input}
        name="propertyValue"
        label="Home value"
        placeholder="850,000"
        value={value || ""}
        onChange={onChange}
        adornment={
          guessedValue?.data ? (
            <Touchable onClick={setToGuessedValue}>
              <Chip className="mr-2" variant="green">
                Set {formatters.number.getCurrencyFromNumber(guessedValue?.data, { digits: 0 })} as value
              </Chip>
            </Touchable>
          ) : null
        }
      />
    </FormFlowPage>
  );
};

export default memo(HomeValue);
